import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Shield, Wrench, Box, Zap, Menu, X, Mail, Phone, MapPin, Calendar } from "lucide-react";
import { HERO_IMAGES } from "@/data/products";
import { SEO } from "@/components/SEO";
import { EnquiryForm } from "@/components/EnquiryForm";

// Redesigned presentation of the existing Bushtech overview content (see
// BushtechOverviewPage.jsx) — same product ranges, same core claims, but
// built as a standalone "manufacturer head office" brand experience
// (Bushtech Canopies) distinct from XpeditioNZ's overland-lifestyle site.
// Product links point at the real, shared product pages/catalog.
const BUSHTECH_PRODUCT_OPTIONS = [
  "Wellside Canopy",
  "X-Series Tray & Canopy",
  "Bush Cabin Ute Camper",
  "Dealer / Trade / Fleet Enquiry",
  "General Enquiry",
];

const RANGES = [
  {
    label: "01",
    title: "WELLSIDE CANOPIES",
    sub: "OEM-grade canopies for trade, fleet & overland builds",
    desc: "NZ's strongest aluminium canopies. Uniquely dust proof. 300kg dynamic load. Lighter than fibreglass. Bigger gullwing doors.",
    image: HERO_IMAGES.wellsideHilux,
    to: "/wellside-canopies",
  },
  {
    label: "02",
    title: "X-SERIES TRAY & CANOPY",
    sub: "Service body engineered for serious expedition use",
    desc: "500kg dynamic load. Tested in Africa. Onboard water, electrical & fitout for true overland builds.",
    image: HERO_IMAGES.xseriesDuo,
    to: "/x-series-tray",
  },
  {
    label: "03",
    title: "BUSH CABIN UTE CAMPER",
    sub: "Live-in comfort. Real 4WD capability.",
    desc: "Sub 200kg fully insulated camper. Internal sleeping access. EEZI-AWN 100mm mattress. From $24,990.",
    image: "/products/bushcabin/731077_a2b10524eafa40c58c0da987bdfe4eea~mv2.jpg",
    imageClassName: "scale-[1.3] object-[50%_55%]",
    to: "/bush-cabin",
  },
];

const PILLARS = [
  { icon: Shield, kpi: "300KG", label: "Dynamic Load Rating", note: "Tested to 2.5 tonnes static" },
  { icon: Wrench, kpi: "TIG", label: "Welded Frame", note: "Not a bolt-together kit" },
  { icon: Box, kpi: "10YR", label: "Frame Guarantee", note: "Manufacturer-backed" },
  { icon: Zap, kpi: "65KG", label: "Typical Weight", note: "Lighter than fibreglass" },
];

const NAV_LINKS = [
  { label: "Wellside Canopies", to: "/wellside-canopies" },
  { label: "X-Series Tray & Canopy", to: "/x-series-tray" },
  { label: "Bush Cabin", to: "/bush-cabin" },
  { label: "Our Story", to: "#story" },
  { label: "Enquire", to: "#enquire" },
];

export default function BushtechCanopiesHomePage() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div data-testid="bushtech-canopies-home-page" className="bg-white text-[#0A0A0A]" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <SEO
        path="/bushtech-canopies"
        title="Bushtech Canopies NZ — TIG-Welded Aluminium Ute Canopies, Trays & Campers"
        description="The official NZ home of Bushtech aluminium ute canopies — Wellside Canopies, X-Series Tray & Canopy, and the Bush Cabin ute camper. TIG-welded, not bolt-together. Built for the jobsite and the outback."
        noindex
      />

      {/* NAV */}
      <header data-testid="bushtech-nav" className="sticky top-0 z-50 bg-white border-b border-[#0A0A0A]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <a href="#top" data-testid="bushtech-nav-logo" className="flex items-center">
            <img src="/logos/bushtech-logo-white-bg.webp" alt="Bushtech Aluminium Canopies" className="h-8 md:h-10 w-auto" />
          </a>
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.slice(0, 3).map((l) => (
              <Link key={l.to} to={l.to} data-testid={`bushtech-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-xs font-bold uppercase tracking-[0.15em] hover:text-[#CA8A04] transition-colors duration-150">
                {l.label}
              </Link>
            ))}
            <a href="#story" data-testid="bushtech-nav-link-story" className="text-xs font-bold uppercase tracking-[0.15em] hover:text-[#CA8A04] transition-colors duration-150">Our Story</a>
          </nav>
          <a href="#enquire" data-testid="bushtech-nav-enquire" className="hidden lg:inline-flex bg-[#CA8A04] text-[#0A0A0A] font-bold uppercase tracking-wider text-xs px-6 py-3 rounded-none hover:bg-[#A16207] transition-colors duration-150">
            Enquire Now
          </a>
          <button data-testid="bushtech-nav-menu-toggle" onClick={() => setMenuOpen((v) => !v)} className="lg:hidden">
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {menuOpen && (
          <div data-testid="bushtech-nav-mobile-menu" className="lg:hidden border-t border-[#0A0A0A]/10 px-6 py-4 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              l.to.startsWith("#") ? (
                <a key={l.to} href={l.to} onClick={() => setMenuOpen(false)} className="text-sm font-bold uppercase tracking-wider">{l.label}</a>
              ) : (
                <Link key={l.to} to={l.to} onClick={() => setMenuOpen(false)} className="text-sm font-bold uppercase tracking-wider">{l.label}</Link>
              )
            ))}
          </div>
        )}
      </header>

      {/* HERO */}
      <section id="top" data-testid="bushtech-hero" className="relative min-h-[90svh] flex items-end overflow-hidden bg-[#0A0A0A]">
        <div className="absolute inset-0">
          <img src={HERO_IMAGES.wellside} alt="Bushtech Wellside canopy fitted to a ute" className="w-full h-full object-cover object-[65%_20%] opacity-95" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] from-0% via-black/65 via-50% to-transparent md:via-black/45 md:via-30%" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-10 md:pb-20 w-full">
          <p className="text-[10px] md:text-[11px] font-bold uppercase tracking-[0.12em] md:tracking-[0.25em] text-[#CA8A04] mb-3 md:mb-4">— Proudly Distributed in NZ by XpeditioNZ</p>
          <h1 style={{ fontFamily: "'Barlow Condensed', sans-serif" }} className="text-white uppercase font-900 leading-[0.95] tracking-tight text-4xl sm:text-5xl md:text-[5vw] max-w-[20ch]">
            THE CANOPY <span className="text-[#CA8A04]">TRADIES AND TRAVELLERS</span> TRUST.
          </h1>
          <p className="mt-4 md:mt-5 text-sm md:text-lg text-white/85 max-w-xl leading-relaxed">
            Genuine OEM-quality aluminium canopies, trays and ute campers. Every Bushtech canopy is built around a fully TIG-welded frame — not a cheap bolt-together kit. Engineered in South Africa, proven globally.
          </p>
          <div className="mt-6 md:mt-8 flex flex-wrap gap-3">
            <a href="#ranges" data-testid="bushtech-hero-cta" className="inline-flex items-center gap-2 bg-[#CA8A04] text-[#0A0A0A] font-bold uppercase tracking-wider text-sm px-7 py-4 rounded-none hover:bg-[#A16207] transition-colors duration-150">
              Explore The Range <ArrowUpRight size={16} />
            </a>
            <a href="#enquire" data-testid="bushtech-hero-secondary-cta" className="inline-flex items-center gap-2 bg-transparent text-white border border-white/40 font-bold uppercase tracking-wider text-sm px-7 py-4 rounded-none hover:bg-white hover:text-[#0A0A0A] transition-colors duration-150">
              Talk To Us
            </a>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-[#0A0A0A]/[0.04] border-y border-[#0A0A0A]/15">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 border-l border-[#0A0A0A]/15">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} data-testid={`bushtech-pillar-${i}`} className="p-8 md:p-10 border-r border-b lg:border-b-0 border-[#0A0A0A]/15">
                <Icon size={26} className="text-[#CA8A04] mb-6" strokeWidth={1.5} />
                <p style={{ fontFamily: "'Barlow Condensed', sans-serif" }} className="text-4xl md:text-5xl font-800 leading-none">{p.kpi}</p>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#0A0A0A]/60 mt-4">{p.label}</p>
                <p className="text-sm text-[#0A0A0A]/40 mt-1">{p.note}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* RANGES */}
      <section id="ranges" className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#A16207] mb-4">— Product Lines</p>
            <h2 style={{ fontFamily: "'Barlow Condensed', sans-serif" }} className="text-5xl md:text-6xl font-900 uppercase leading-[0.95]">
              THREE PLATFORMS.<br />ONE STANDARD.
            </h2>
          </div>
          <p className="text-[#0A0A0A]/60 max-w-md text-base leading-relaxed">
            From everyday trade canopies to true expedition-grade campers — every Bushtech build shares the same TIG-welded aluminium DNA.
          </p>
        </div>

        <div data-testid="bushtech-ranges-demo-note" className="flex items-start gap-3 border border-[#CA8A04]/40 bg-[#CA8A04]/5 px-4 py-3 mb-10 max-w-2xl">
          <MapPin size={18} className="text-[#A16207] mt-0.5 shrink-0" strokeWidth={1.8} />
          <p className="text-sm text-[#0A0A0A]/80 leading-snug">
            <span className="font-semibold text-[#0A0A0A]">Demonstrator vehicles for all three platforms</span> are available to view by appointment in Christchurch.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 border-t border-l border-[#0A0A0A]/15">
          {RANGES.map((c, i) => (
            <Link
              key={i}
              to={c.to}
              data-testid={`bushtech-range-card-${i}`}
              className="group relative block border-r border-b border-[#0A0A0A]/15 hover:bg-[#F3F4F6] transition-colors duration-150"
            >
              <div className="aspect-[4/3] overflow-hidden relative">
                <img src={c.image} alt={c.title} className={`w-full h-full object-cover ${c.imageClassName || ""}`} />
                <span className="absolute top-4 left-4 text-[10px] font-bold tracking-[0.25em] uppercase text-[#0A0A0A] bg-[#CA8A04] px-2.5 py-1">{c.label}</span>
              </div>
              <div className="p-7 md:p-8">
                <h3 style={{ fontFamily: "'Barlow Condensed', sans-serif" }} className="text-2xl md:text-3xl font-800 uppercase leading-tight">{c.title}</h3>
                <p className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/50 mt-2">{c.sub}</p>
                <p className="text-[#0A0A0A]/70 mt-4 leading-relaxed text-sm">{c.desc}</p>
                <span className="inline-flex items-center gap-2 mt-6 text-xs font-bold uppercase tracking-[0.15em] group-hover:text-[#A16207] transition-colors duration-150">
                  Explore Range <ArrowUpRight size={14} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* DUAL USE */}
      <section data-testid="bushtech-dual-use" className="border-t border-[#0A0A0A]/10">
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="relative aspect-[4/3] md:aspect-auto md:h-[560px] overflow-hidden">
            <img src="/products/wellsides/731077_965bc7e8fce44fa38c6bb966e7a68bdf~mv2.jpg" alt="Work ute with canopy on a jobsite" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black from-0% via-black/55 via-35% to-transparent" />
            <div className="absolute bottom-0 left-0 p-8 md:p-10">
              <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#CA8A04] mb-3">— The Jobsite</p>
              <h3 style={{ fontFamily: "'Barlow Condensed', sans-serif" }} className="text-white text-3xl md:text-4xl font-800 uppercase leading-tight">ENGINEERED FOR<br />THE WORKDAY.</h3>
            </div>
          </div>
          <div className="relative aspect-[4/3] md:aspect-auto md:h-[560px] overflow-hidden">
            <img src="/products/wellsides/731077_a07494c2652a48a5ac812c078e8dfeb8~mv2.jpg" alt="Bushtech canopy on an overland touring vehicle" className="w-full h-full object-cover object-center scale-[1.35] -translate-y-[14%]" />
            <div className="absolute inset-0 bg-gradient-to-t from-black from-0% via-black/55 via-35% to-transparent" />
            <div className="absolute bottom-0 left-0 p-8 md:p-10">
              <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#CA8A04] mb-3">— The Escape</p>
              <h3 style={{ fontFamily: "'Barlow Condensed', sans-serif" }} className="text-white text-3xl md:text-4xl font-800 uppercase leading-tight">BUILT FOR<br />OVERLAND.</h3>
            </div>
          </div>
        </div>
      </section>

      {/* MANUFACTURER STORY */}
      <section id="story" data-testid="bushtech-story" className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#CA8A04] mb-5">— The Standard</p>
            <h2 style={{ fontFamily: "'Barlow Condensed', sans-serif" }} className="text-4xl md:text-5xl font-900 uppercase leading-[0.95] mb-6">
              SOUTH AFRICAN-ENGINEERED.<br />TIG-WELDED ALUMINIUM.<br /><span className="text-[#CA8A04]">PROVEN QUALITY.</span>
            </h2>
            <div className="space-y-5 text-white/70 leading-relaxed">
              <p>
                Most aluminium ute canopies fall into two camps — low-cost bolt-together kits built to a price, or premium options at a significant jump in cost. <span className="text-white font-medium">Bushtech sits in neither.</span>
              </p>
              <p>
                Built around a fully TIG-welded aluminium frame, every Bushtech canopy delivers OEM-grade construction, class-leading dust and water sealing, and a 10-year manufacturer frame guarantee — originally engineered in South Africa for permanently off-road fleets, now trusted by NZ new-vehicle dealerships, tradies and overlanders alike.
              </p>
              <p>
                <span className="text-white font-medium">Building for the jobsite, or building for the trip of a lifetime?</span> Either way, a Bushtech canopy is the right call — and as Bushtech's official NZ distributor, we sell, fit and stand behind every unit direct.
              </p>
              <p>
                Sorting the rest of your fitout — drawers, rooftop tents, awnings, recovery gear? Visit our sister site, <a href="https://xpeditionz.co.nz" target="_blank" rel="noopener" data-testid="bushtech-story-xpeditionz-link" className="inline-flex items-center align-middle hover:opacity-80 transition-opacity"><img src="/logos/xpeditionz-logo.png" alt="XpeditioNZ" className="h-5 md:h-6 w-auto inline-block" /></a>, for equally exceptional South African-made, world-brand gear built to the same standard.
              </p>
            </div>
          </div>
          <div className="relative aspect-[4/3] overflow-hidden">
            <img src="/products/bushcabin/bushtech-lc79-spare-wheel.webp" alt="Bushtech-branded Land Cruiser 79 with dual spare wheel carrier off-road in South Africa" className="w-full h-full object-cover object-[50%_45%]" />
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="enquire" data-testid="bushtech-contact" className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#CA8A04] mb-5">— Get In Touch</p>
            <h2 style={{ fontFamily: "'Barlow Condensed', sans-serif" }} className="text-4xl md:text-6xl font-900 uppercase leading-[0.95] mb-6">
              READY TO SPEC<br />YOUR CANOPY?
            </h2>
            <p className="text-white/70 leading-relaxed mb-8 max-w-md">
              We'll help you pick the right build for your ute and how you actually use it — trade, fleet or overland.
            </p>
            <div data-testid="bushtech-demo-callout" className="flex items-start gap-3 border border-[#CA8A04]/50 bg-[#CA8A04]/10 px-4 py-4 mb-8">
              <MapPin size={20} className="text-[#CA8A04] mt-0.5 shrink-0" strokeWidth={1.6} />
              <p className="text-sm md:text-base leading-snug">
                <span className="font-semibold text-[#CA8A04]">Bushtech demonstrator vehicles</span> are available to view in Christchurch — get in touch to arrange a viewing.
              </p>
            </div>
            <div className="space-y-3 text-sm text-white/70">
              <div className="flex items-start gap-3"><Mail size={14} className="mt-1 text-[#CA8A04]" /><a href="mailto:support@xpeditionz.co.nz" className="hover:text-white transition-colors">support@xpeditionz.co.nz</a></div>
              <div className="flex items-start gap-3"><Phone size={14} className="mt-1 text-[#CA8A04]" /><a href="tel:+64284349101" className="hover:text-white transition-colors">028 434 9101</a></div>
              <div className="flex items-start gap-3"><MapPin size={14} className="mt-1 text-[#CA8A04]" /><span>Christchurch, NZ</span></div>
              <div className="flex items-start gap-3"><Calendar size={14} className="mt-1 text-[#CA8A04]" /><span>Demo vehicles by appointment</span></div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div
              className="border border-white/15 bg-white/[0.03] p-6 md:p-10"
              style={{
                "--brand-primary": "#CA8A04",
                "--brand-primary-hover": "#A16207",
                "--bg-main": "#0A0A0A",
                "--border-default": "rgba(255,255,255,0.15)",
                "--text-tertiary": "rgba(255,255,255,0.4)",
              }}
            >
              <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#CA8A04] mb-2">— Send An Enquiry</p>
              <p className="text-white text-sm mb-6">Trade, fleet & dealer enquiries welcome.</p>
              <EnquiryForm idPrefix="bushtech-enquiry" defaultProduct="Wellside Canopy" productOptions={BUSHTECH_PRODUCT_OPTIONS} />
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer data-testid="bushtech-footer" className="border-t border-[#0A0A0A]/10 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <span style={{ fontFamily: "'Barlow Condensed', sans-serif" }} className="text-lg font-800 uppercase tracking-tight">BUSHTECH CANOPIES</span>
          <div className="flex flex-wrap items-center justify-center gap-6" data-testid="bushtech-footer-legal-links">
            <Link to="/privacy-policy" data-testid="bushtech-footer-privacy-link" className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#0A0A0A]/50 hover:text-[#0A0A0A] transition-colors">Privacy Policy</Link>
            <Link to="/shipping-policy" data-testid="bushtech-footer-shipping-link" className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#0A0A0A]/50 hover:text-[#0A0A0A] transition-colors">Shipping Policy</Link>
            <Link to="/returns-policy" data-testid="bushtech-footer-returns-link" className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#0A0A0A]/50 hover:text-[#0A0A0A] transition-colors">Returns & Warranty Policy</Link>
          </div>
        </div>
        <div className="border-t border-[#0A0A0A]/10">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#0A0A0A]/40">
              &copy; {new Date().getFullYear()} Bushtech Canopies &mdash; NZ Distributor: XpeditioNZ
            </p>
            <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#0A0A0A]/40">
              Built In South Africa &middot; Proven Globally
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
